import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IS_MASTER, api } from '../lib/api';
import { inr } from '../lib/format';
import { Badge, Button, Card, CardHead, Empty, Field, Input, Modal, Notice, useToast } from '../components/ui';

interface Setting {
  key: string;
  value: unknown;
  description: string | null;
  source: 'database' | 'environment' | 'default';
}

/** Human wording for each key, so the console is not a list of dotted paths. */
/**
 * `kind` is for values that are not numbers. Without it the editor turns
 * anything that looks like a number into one, which is wrong for a phone
 * number: it would lose its leading plus, or its leading zero.
 */
const LABELS: Record<
  string,
  { label: string; help: string; unit?: 'paise' | 'days' | 'metres' | 'percent'; kind?: 'text' | 'list' }
> = {
  'app.support_phone': {
    label: 'Support phone',
    help: 'Shown on Help & support with a Call button. Leave blank to hide it.',
    kind: 'text',
  },
  'app.support_whatsapp': {
    label: 'Support WhatsApp',
    help: 'Number to message on WhatsApp, with the country code. Leave blank to hide it.',
    kind: 'text',
  },
  'app.support_email': {
    label: 'Support email',
    help: 'Shown on Help & support. Leave blank to hide it.',
    kind: 'text',
  },
  'app.support_hours': {
    label: 'Support hours',
    help: 'When support answers, in your own words — e.g. Mon–Sat, 9 am to 6 pm.',
    kind: 'text',
  },
  'app.terms_url': {
    label: 'Terms of Service link',
    help: 'Opened from the sign-in screen. A full address starting with https://',
    kind: 'text',
  },
  'app.privacy_url': {
    label: 'Privacy Policy link',
    help: 'Opened from the sign-in screen. A full address starting with https://',
    kind: 'text',
  },
  'app.maintenance_mode': {
    label: 'Maintenance mode',
    help: 'When on, the app shows the maintenance message instead of its screens. Everybody is affected.',
  },
  'app.maintenance_message': {
    label: 'Maintenance message',
    help: 'What people read while maintenance mode is on.',
    kind: 'text',
  },
  'app.popular_searches': {
    label: 'Popular searches',
    help: 'Suggestions under the search box. Up to twelve, separated by commas.',
    kind: 'list',
  },
  'app.listing_packers_movers': {
    label: 'Packers & Movers listings',
    help: 'When on, people can post under Packers & Movers. When off, the row says Soon.',
  },
  'app.listing_travels': {
    label: 'Travels listings',
    help: 'When on, people can post under Travels. When off, the row says Soon.',
  },
  'gap.manager_mobiles': {
    label: 'GAP owner mobile number',
    help: 'Signed in by OTP with this number, the account sees the Global Axis Partner owner dashboard and is the only one that can choose Customer or Employee and generate links. Separate several with commas.',
    kind: 'list',
  },
  'gap.chain_commission_paise': {
    label: 'Global Customer commission',
    help: 'What a Customer member earns when somebody joins through their link and the ₹2,000 is verified.',
    unit: 'paise',
  },
  'gap.customer_reward_once': {
    label: 'Customer commission once only',
    help: 'On: a Customer member earns it for their first direct referral only. Off: for every eligible direct referral. Never up the chain either way.',
  },
  'gap.employee_commission_paise': {
    label: 'Global Employee commission',
    help: 'What an Employee member earns for every member who joins through their link.',
    unit: 'paise',
  },
  'ranking.seller_rank_enabled': {
    label: 'Rank sellers by their ads',
    help: 'On: a seller with more ads up has theirs shown higher in listings and search. Paid boosts still come first. Off: newest first after boosts.',
  },
  'ranking.listings_per_step': {
    label: 'Ads per rank step',
    help: 'A seller goes up one rank for every this many live ads. 1 ranks every extra ad.',
  },
  'ranking.max_rank': {
    label: 'Top rank',
    help: 'The highest rank a seller can reach, however many ads they have up.',
  },
  'partners.registration_fee_paise': {
    label: 'Partner registration fee',
    help: 'What somebody pays to become a Globaxis Partner.',
    unit: 'paise',
  },
  'partners.registration_gst_percent': {
    label: 'GST on partner registration',
    help: 'Added on top of the registration fee. 0 means the fee is all-in.',
    unit: 'percent',
  },
  'search.provider_radius_meters': {
    label: 'Provider search radius',
    help: 'How far a customer sees live providers. FR-SP-06 specifies 20 km.',
    unit: 'metres',
  },
  'listings.lifetime_days': {
    label: 'Listing lifetime',
    help: 'How long a listing stays live before moving to hold.',
    unit: 'days',
  },
  'listings.hold_days': {
    label: 'Hold window',
    help: 'How long a held listing waits before being deleted. It stays recoverable throughout.',
    unit: 'days',
  },
  'agents.listing_slots': {
    label: 'Partner listing slots',
    help: 'Free listing slots each partner gets while their membership is paid (or waived) and running (FR-PANEL-03).',
  },
  'partners.validity_days': {
    label: 'Partner membership validity',
    help: 'Days a paid ₹2,000 membership lasts; Pay Now renews it after. Applies to payments made after a change. Waived memberships do not expire.',
    unit: 'days',
  },
  'agents.referral_bonus_paise': {
    label: 'Referral bonus',
    help: 'Credited to a sponsor on enrolment. Accrues but is not paid while payouts are disabled.',
    unit: 'paise',
  },
  'agents.renewal_bonus_paise': {
    label: 'Renewal bonus',
    help: 'Credited when a downline renews.',
    unit: 'paise',
  },
  'billing.default_gst_percent': { label: 'Default GST', help: 'Applied where a plan does not set its own.', unit: 'percent' },
  'categories.auto_append_needs_approval': {
    label: 'Moderate new categories',
    help: 'When off, a name typed under “Others” is published to everyone instantly — the literal SRD behaviour, and what TR-5 warns about.',
  },
  'listings.max_images': { label: 'Images per listing', help: '' },
  'listings.max_videos': { label: 'Videos per listing', help: '' },
  'slots.free_duration_days': {
    label: 'Free slot duration',
    help: 'How long the one free slot every account gets stays open, from the day it is granted.',
    unit: 'days',
  },
  'slots.free_extension_days': {
    label: 'Free slot extension',
    help: 'The default length of one extension. An admin can override it per extension.',
    unit: 'days',
  },
  'jobs.requires_approval': {
    label: 'Approve jobs before they are shown',
    help: 'When on, a job an employer publishes waits under Jobs → Pending approval until an admin approves it; its run starts then. When off, jobs go live as soon as they are published.',
  },
  'verification.kyc_required': {
    label: 'Require KYC',
    help: 'On hold. When off, nobody is asked to verify before posting an ad, going live as a provider or enrolling a partner; verifying stays available. Turn on to require it again.',
  },
  'verification.police_required': {
    label: 'Require police verification',
    help: 'On hold. When off, local and home service providers go live without a police certificate; uploading one stays available. Turn on to require it again.',
  },
  'slots.free_max_extensions': {
    label: 'Maximum extensions',
    help: 'How many times one free slot may be extended. 0 means no limit.',
  },
};

const SOURCE_TONE = { database: 'blue', environment: 'purple', default: 'neutral' } as const;

export function SettingsPage() {
  const [editing, setEditing] = useState<Setting | null>(null);
  const [draft, setDraft] = useState('');
  const toast = useToast();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({ queryKey: ['settings'], queryFn: () => api.get<Setting[]>('/admin/settings') });

  const save = useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) => api.patch(`/admin/settings/${key}`, { value }),
    onSuccess: () => {
      toast('Setting saved — it takes effect within 30 seconds', 'success');
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  function render(s: Setting) {
    const meta = LABELS[s.key];
    if (typeof s.value === 'boolean') return <Badge tone={s.value ? 'green' : 'neutral'}>{s.value ? 'On' : 'Off'}</Badge>;
    if (Array.isArray(s.value)) return s.value.length ? s.value.join(', ') : <span className="cell-muted">None</span>;
    if (meta?.kind === 'text') return s.value ? String(s.value) : <span className="cell-muted">Not set</span>;
    const n = Number(s.value);
    if (meta?.unit === 'paise' && Number.isFinite(n)) return inr(n);
    if (meta?.unit === 'metres' && Number.isFinite(n)) return `${(n / 1000).toFixed(1)} km`;
    if (meta?.unit === 'days') return `${s.value} days`;
    if (meta?.unit === 'percent') return `${s.value}%`;
    return String(s.value);
  }

  return (
    <div className="stack">
      <Notice tone="info" title="Database wins, then environment, then the built-in default">
        A key with no row here behaves exactly as it did before this table existed. Saving one takes effect within
        thirty seconds without a restart.
      </Notice>

      <Card>
        <CardHead
          title="Profit distribution & reveal"
          sub="v2 §28 — annual profit share, and who can see it"
          action={
            <Link to="/settings/profit">
              <Button size="sm">Open</Button>
            </Link>
          }
        />
        <p style={{ margin: 0, lineHeight: 1.65, fontSize: 13.6, color: 'var(--ink-muted)' }}>
          Category shares, eligibility rules, the annual calculation and the reveal control. Distributions are hidden
          from members until a super admin reveals them; every calculation, distribution, reveal and hide is recorded
          in the audit log.
        </p>
      </Card>

      {!IS_MASTER && (
        <Card>
          <CardHead
            title="Free Post"
            sub="Every account's free ad: on or off, validity, allowance, categories, approval, button text"
            action={
              <Link to="/free-posts">
                <Button size="sm">Open</Button>
              </Link>
            }
          />
          <p style={{ margin: 0, lineHeight: 1.65, fontSize: 13.6, color: 'var(--ink-muted)' }}>
            Set on the Free Post Management page, where the free ads themselves are approved, edited, suspended or
            removed.
          </p>
        </Card>
      )}

      {[
        {
          title: 'Verification',
          sub: 'On hold for now. Turn a check on and the app asks for it again within thirty seconds, with no update.',
          rows: (data ?? []).filter((s) => s.key.startsWith('verification.')),
        },
        {
          title: 'Mobile app',
          sub: 'What the app shows and does. The app reads these when it starts.',
          rows: (data ?? []).filter((s) => s.key.startsWith('app.')),
        },
        {
          title: 'Global Axis Partner',
          sub: 'Who the owner is, and what each programme pays. Changes are on the audit log.',
          rows: (data ?? []).filter((s) => s.key.startsWith('gap.')),
        },
        {
          title: 'Ad ranking',
          sub: 'Paid boosts come first (prices and durations on Plans), then sellers with more ads up, then the newest. Updated every five minutes.',
          rows: (data ?? []).filter((s) => s.key.startsWith('ranking.')),
        },
        {
          title: 'Operational settings',
          sub: 'v2 §38',
          rows: (data ?? []).filter(
            (s) =>
              !s.key.startsWith('app.') &&
              !s.key.startsWith('verification.') &&
              !s.key.startsWith('free_post.') &&
              !s.key.startsWith('gap.') &&
              !s.key.startsWith('ranking.') &&
              // Its own page: Contact visibility.
              !s.key.startsWith('contact.'),
          ),
        },
      ].map((group) => (
      <Card flush key={group.title}>
        <CardHead title={group.title} sub={group.sub} />
        {isLoading ? (
          <div style={{ padding: 'var(--s5)' }}>
            <div className="skeleton" style={{ height: 200 }} />
          </div>
        ) : !group.rows.length ? (
          <Empty icon="⚙" title="No settings exposed" />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Setting</th>
                  <th>Value</th>
                  <th>Source</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {group.rows.map((s) => {
                  const meta = LABELS[s.key];
                  return (
                    <tr key={s.key}>
                      <td>
                        <div className="cell-strong">{meta?.label ?? s.key}</div>
                        <div className="cell-muted">{meta?.help ?? s.key}</div>
                        <div className="cell-mono">{s.key}</div>
                      </td>
                      <td className="cell-strong">{render(s)}</td>
                      <td>
                        <Badge tone={SOURCE_TONE[s.source]}>{s.source}</Badge>
                      </td>
                      <td>
                        <div className="cell-actions">
                          <Button
                            size="sm"
                            onClick={() => {
                              setEditing(s);
                              setDraft(Array.isArray(s.value) ? s.value.join(', ') : String(s.value ?? ''));
                            }}
                          >
                            Change
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      ))}

      {editing && (
        <Modal
          title={LABELS[editing.key]?.label ?? editing.key}
          sub={LABELS[editing.key]?.help}
          onClose={() => setEditing(null)}
          footer={
            <>
              <Button onClick={() => setEditing(null)}>Cancel</Button>
              <Button
                variant="primary"
                loading={save.isPending}
                onClick={() => {
                  const raw = draft.trim();
                  const kind = LABELS[editing.key]?.kind;
                  // Text and lists go as typed: the server checks and tidies them.
                  const value = kind
                    ? raw
                    : raw === 'true'
                      ? true
                      : raw === 'false'
                        ? false
                        : Number.isFinite(Number(raw)) && raw !== ''
                          ? Number(raw)
                          : raw;
                  save.mutate({ key: editing.key, value });
                }}
              >
                Save
              </Button>
            </>
          }
        >
          <div className="stack">
            {typeof editing.value === 'boolean' ? (
              <Field label="Value">
                <div className="chips">
                  <button className={`chip ${draft === 'true' ? 'chip--active' : ''}`} onClick={() => setDraft('true')}>
                    On
                  </button>
                  <button className={`chip ${draft === 'false' ? 'chip--active' : ''}`} onClick={() => setDraft('false')}>
                    Off
                  </button>
                </div>
              </Field>
            ) : (
              <Field
                label="Value"
                hint={
                  LABELS[editing.key]?.unit === 'paise'
                    ? `In paise. ${draft && Number.isFinite(Number(draft)) ? inr(Number(draft)) : ''}`
                    : LABELS[editing.key]?.unit === 'metres'
                      ? `In metres. ${draft && Number.isFinite(Number(draft)) ? `${(Number(draft) / 1000).toFixed(1)} km` : ''}`
                      : undefined
                }
              >
                <Input value={draft} autoFocus onChange={(e) => setDraft(e.target.value)} />
              </Field>
            )}
            <div className="cell-mono" style={{ color: 'var(--ink-faint)' }}>
              {editing.key}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
